import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { readReturnTo } from '../lib/returnTo.js';
import { useBranding } from '../lib/branding.js';
import { useCapabilities } from '../lib/capabilities.js';
import { ApiError } from '../lib/api.js';
import { PasswordInput } from '../components/ui.js';
import { GoogleSignInButton } from '../components/GoogleSignInButton.js';

/**
 * Self-service sign-up. Only usable when the server reports `signup`
 * (SIGNUP_ENABLED); "Sign up with Google" posts to the same endpoint as sign-in,
 * which creates the account when the address is new.
 */
export function SignupScreen(): ReactNode {
  const { user, register, loginWithGoogle } = useAuth();
  const { branding } = useBranding();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(readReturnTo('/displays'), { replace: true });
  }, [user, navigate]);

  async function run(attempt: () => Promise<void>): Promise<void> {
    setError(undefined);
    setBusy(true);
    try {
      await attempt();
      navigate(readReturnTo('/displays'), { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.fieldMessage() ?? err.message) : 'Could not reach the server.',
      );
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (password !== confirm) {
      setError('The passwords do not match.');
      return;
    }
    void run(() => register({ name, email, password, passwordConfirmation: confirm }));
  }

  const googleClientId = caps?.googleClientId ?? null;
  const closed = caps != null && !caps.signup;

  return (
    <div
      className="auth-wrap"
      style={
        branding.loginBackgroundUrl
          ? {
              backgroundImage: `url(${branding.loginBackgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      <form className="card card-pad auth-card stack" onSubmit={submit}>
        <div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              style={{ maxHeight: 40, maxWidth: '70%', marginBottom: 4 }}
            />
          ) : (
            <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          )}
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Create your account
          </p>
        </div>
        {closed ? (
          <div className="form-error">
            Sign-up isn&apos;t open on this server. Ask an administrator to invite you.
          </div>
        ) : null}
        {googleClientId && !closed ? (
          <>
            <div className="stack small">
              <GoogleSignInButton
                clientId={googleClientId}
                text="signup_with"
                disabled={busy}
                onCredential={(idToken) => void run(() => loginWithGoogle(idToken, true))}
                onError={setError}
              />
            </div>
            <div className="row" style={{ alignItems: 'center', gap: 8 }}>
              <hr style={{ flex: 1 }} />
              <span className="muted small">or</span>
              <hr style={{ flex: 1 }} />
            </div>
          </>
        ) : null}
        <label className="field">
          Name
          <input
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="field">
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="field">
          Password
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            required
          />
        </label>
        <label className="field">
          Confirm password
          <PasswordInput
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
            required
          />
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="btn primary" type="submit" disabled={busy || closed}>
          {busy ? 'Creating account…' : 'Create account'}
        </button>
        <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
